<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue'

import { useReducedMotion } from '@/composables/useReducedMotion'
import { useSteadyValue } from '@/composables/useSteadyValue'

import { LIVE_TEXT_DWELL_MS, differsOnlyInNumbers, splitNumbers } from './live-text'

const props = withDefaults(
  defineProps<{
    /** The line to show. `null` shows nothing, and the line eases its height to zero. */
    text: string | null
    /** Rolls only the numbers that changed when the words around them stay the same, as in a count. */
    numbers?: boolean
    /** Classes for the text itself: its type, colour and clamp, and any space above it, which then collapses with the line. */
    lineClass?: string
    /** Classes for an inner span around the words, for an effect that must not share the line's own animation, such as a shimmer. */
    textClass?: string
  }>(),
  { numbers: false, lineClass: '', textClass: '' },
)

type Phase = 'rest' | 'in' | 'out'

interface Digit {
  key: number
  text: string
  phase: Phase
}

type Part = { kind: 'words'; text: string } | { kind: 'number'; digits: Digit[] }

interface Line {
  key: number
  phase: Phase
  parts: Part[]
}

const LEAVE_FALLBACK_MS = 400

const shown = useSteadyValue(() => props.text, LIVE_TEXT_DWELL_MS)
const reduced = useReducedMotion()
const root = useTemplateRef<HTMLElement>('root')
const lines = ref<Line[]>([])
const height = ref<number | null>(null)
const timers = new Set<ReturnType<typeof setTimeout>>()
let nextKey = 0

function toParts(text: string, phase: Phase): Part[] {
  if (!props.numbers) return [{ kind: 'words', text }]
  return splitNumbers(text).map((part) =>
    part.kind === 'words'
      ? part
      : { kind: 'number', digits: [{ key: nextKey++, text: part.text, phase }] },
  )
}

function later(run: () => void): void {
  const timer = setTimeout(() => {
    timers.delete(timer)
    run()
  }, LEAVE_FALLBACK_MS)
  timers.add(timer)
}

function removeLine(line: Line): void {
  lines.value = lines.value.filter((entry) => entry !== line)
}

function removeDigit(part: Extract<Part, { kind: 'number' }>, digit: Digit): void {
  part.digits = part.digits.filter((entry) => entry !== digit)
}

function onLineEnd(line: Line): void {
  if (line.phase === 'out') removeLine(line)
}

function onDigitEnd(part: Extract<Part, { kind: 'number' }>, digit: Digit): void {
  if (digit.phase === 'out') removeDigit(part, digit)
}

function rollNumbers(next: string): void {
  const current = lines.value.find((line) => line.phase !== 'out')
  if (!current) return
  const incoming = splitNumbers(next)
  current.parts.forEach((part, index) => {
    const target = incoming[index]!
    if (part.kind !== 'number') return
    const live = part.digits.find((digit) => digit.phase !== 'out')
    if (!live || live.text === target.text) return
    live.phase = 'out'
    later(() => removeDigit(part, live))
    part.digits.push({ key: nextKey++, text: target.text, phase: 'in' })
  })
}

async function swapLine(next: string | null): Promise<void> {
  const el = root.value
  if (!el) return
  const from = el.offsetHeight
  for (const line of lines.value) {
    if (line.phase === 'out') continue
    line.phase = 'out'
    later(() => removeLine(line))
  }
  if (next !== null) lines.value.push({ key: nextKey++, phase: 'in', parts: toParts(next, 'rest') })
  if (reduced.value) return
  height.value = from
  await nextTick()
  const entering = next === null ? null : (el.lastElementChild as HTMLElement | null)
  const to = entering?.offsetHeight ?? 0
  height.value = to === from ? null : to
}

function onHeightEnd(event: TransitionEvent): void {
  if (event.propertyName === 'height' && height.value !== 0) height.value = null
}

watch(shown, (next, previous) => {
  const onlyNumbers =
    props.numbers && next !== null && previous !== null && differsOnlyInNumbers(previous, next)
  if (onlyNumbers && next !== null && !reduced.value) {
    rollNumbers(next)
    return
  }
  void swapLine(next)
})

if (shown.value !== null) {
  lines.value = [{ key: nextKey++, phase: 'rest', parts: toParts(shown.value, 'rest') }]
}

onBeforeUnmount(() => timers.forEach((timer) => clearTimeout(timer)))
</script>

<template>
  <div class="w-full">
    <div
      ref="root"
      class="live-text"
      :data-sizing="height === null ? undefined : 'true'"
      :style="height === null ? undefined : { height: `${height}px` }"
      aria-hidden="true"
      @transitionend.self="onHeightEnd"
    >
      <span
        v-for="line in lines"
        :key="line.key"
        class="live-text__line"
        :class="lineClass"
        :data-phase="line.phase"
        @animationend.self="onLineEnd(line)"
      >
        <template v-for="(part, index) in line.parts" :key="index">
          <template v-if="part.kind === 'words'">
            <span v-if="textClass" :class="textClass">{{ part.text }}</span>
            <template v-else>{{ part.text }}</template>
          </template>
          <span v-else class="live-text__number">
            <span
              v-for="digit in part.digits"
              :key="digit.key"
              :data-phase="digit.phase"
              @animationend.self="onDigitEnd(part, digit)"
              >{{ digit.text }}</span
            >
          </span>
        </template>
      </span>
    </div>
    <span class="sr-only">{{ shown }}</span>
  </div>
</template>
